import { useRouter } from 'next/router'
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { LoadingOverlay } from 'src/components/Loaders/Loader'

type ILoaderContext = {
  loading: boolean
  setLoading: (loading: boolean) => void
  redirecting: boolean
  setRedirecting: (loading: boolean) => void
}

export const LoaderContext = createContext<ILoaderContext>({
  loading: false,
  setLoading: () => {},
  redirecting: false,
  setRedirecting: () => {}
})

const LoaderProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [redirecting, setRedirecting] = useState(false)

  useEffect(() => {
    /* removes loader when landed on new page and rendered */
    setRedirecting(false)
  }, [router.query])

  useEffect(() => {
    // A failed or cancelled navigation must release the blocking overlay too.
    const finishRedirect = () => setRedirecting(false)
    router.events.on('routeChangeComplete', finishRedirect)
    router.events.on('routeChangeError', finishRedirect)
    router.events.on('hashChangeComplete', finishRedirect)
    return () => {
      router.events.off('routeChangeComplete', finishRedirect)
      router.events.off('routeChangeError', finishRedirect)
      router.events.off('hashChangeComplete', finishRedirect)
    }
  }, [router.events])

  const value = useMemo(
    () => ({
      loading,
      setLoading,
      redirecting,
      setRedirecting
    }),
    [loading, setLoading, redirecting, setRedirecting]
  )

  return (
    <LoaderContext.Provider value={value}>
      {children}
      {(loading || redirecting) && <LoadingOverlay />}
    </LoaderContext.Provider>
  )
}

export default React.memo(LoaderProvider)

export const useLoaderProvider = (): ILoaderContext => useContext(LoaderContext)
